import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Camera, ChevronLeft, ChevronRight, ImageOff, Trash2, ExternalLink } from 'lucide-react';
import { useJobPhotos, useDeleteJobPhoto, type JobPhoto } from '@/hooks/useJobPhotos';
import { useUserRole } from '@/hooks/useUserRole';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';

interface JobPhotosCardProps {
  jobId: string;
}

/**
 * Photos the technician took from the Steddy app: thumbnail grid, lightbox with
 * prev/next, delete for admins. Uploads happen on the device; the office only views.
 */
export function JobPhotosCard({ jobId }: JobPhotosCardProps) {
  const { data: photos = [], isLoading } = useJobPhotos(jobId);
  const deletePhoto = useDeleteJobPhoto();
  const { isAdmin } = useUserRole();
  const { locale, timezone } = useCompanyLocale();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<JobPhoto | null>(null);

  const current = openIndex !== null ? photos[openIndex] ?? null : null;
  const stamp = (value: string) => formatDocDate(value, `${locale.shortDate}, h:mm a`, timezone);

  const step = (delta: number) => {
    if (openIndex === null || photos.length === 0) return;
    setOpenIndex((openIndex + delta + photos.length) % photos.length);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Camera className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-lg">Photos</CardTitle>
          </div>
          {photos.length > 0 && (
            <span className="text-sm text-muted-foreground">{photos.length} from the field</span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-square rounded-md" />)}
          </div>
        ) : photos.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No photos yet. Photos the technician takes in the Steddy app appear here as they upload.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {photos.map((photo, i) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setOpenIndex(i)}
                className="group relative aspect-square overflow-hidden rounded-md border bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={photo.caption ?? stamp(photo.created_at)}
              >
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={photo.caption ?? `Job photo ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ImageOff className="h-5 w-5" />
                  </span>
                )}
                {photo.caption && (
                  <span className="absolute inset-x-0 bottom-0 truncate bg-background/80 px-1.5 py-0.5 text-[10px] text-foreground">
                    {photo.caption}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {/* Lightbox */}
      <Dialog open={current !== null} onOpenChange={(open) => { if (!open) setOpenIndex(null); }}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {current && (
            <>
              <DialogHeader className="px-4 pt-4">
                <DialogTitle className="text-base">
                  {current.caption || `Photo ${(openIndex ?? 0) + 1} of ${photos.length}`}
                </DialogTitle>
                <DialogDescription>
                  {current.tech?.name ? `${current.tech.name} · ` : ''}{stamp(current.created_at)}
                  {current.width && current.height ? ` · ${current.width}×${current.height}` : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="relative flex items-center justify-center bg-muted/40" style={{ minHeight: '40vh' }}>
                {current.url ? (
                  <img src={current.url} alt={current.caption ?? 'Job photo'} className="max-h-[70vh] w-auto max-w-full object-contain" />
                ) : (
                  <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                    <ImageOff className="h-8 w-8" />
                    <p className="text-sm">This photo could not be loaded.</p>
                  </div>
                )}
                {photos.length > 1 && (
                  <>
                    <Button variant="secondary" size="icon" className="absolute left-2 top-1/2 -translate-y-1/2" onClick={() => step(-1)} aria-label="Previous photo">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="secondary" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => step(1)} aria-label="Next photo">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-4 pb-4">
                <span className="text-xs text-muted-foreground">{(openIndex ?? 0) + 1} / {photos.length}</span>
                <div className="flex items-center gap-2">
                  {current.url && (
                    <Button asChild variant="outline" size="sm">
                      <a href={current.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                        Open full size
                      </a>
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setPendingDelete(current)}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              The photo is removed from storage and from the job. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!pendingDelete) return;
                deletePhoto.mutate(
                  { photo: pendingDelete },
                  {
                    onSuccess: () => {
                      setPendingDelete(null);
                      setOpenIndex(null);
                    },
                  },
                );
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
